import PageLayout from '@components/PageLayout';
import Step1Contact from '@components/my-prescriptions/prescription-contact/Step1Contact';
import Step2Contact from '@components/my-prescriptions/prescription-contact/Step2Contact';
import ArrowLeft from '@images/svg-icons/arrowLeft.svg';
import ContactLenses from '@images/svg-icons/prescription-contact.svg';
import InfoIcon from '@images/svg-icons/infoIcon.svg';
import BackButton from '@images/svg-icons/backButtonContactStep.svg';
import BackButtonMobile from '@images/svg-icons/backButtonContactStepMobile.svg';
import {
    IContactLensDetails,
    IMappedContactLensItem,
    IMySavedPrescriptionItem,
    IMySavedPrescriptionsListPayload,
} from '@interfaces/myAppointmentInterfaces';
import * as actions from '@redux-actions/';
import { getCLDetailsSingle, getMySavedPrescriptions } from '@services/myAppointmentServices';
import { getLabelFallback } from '@utils/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';
import { RootState } from 'src/store/reducers';
import Modal from '@components/styled-UI/Modal';

type Props = {
    isEditingPrescription: boolean;
};

const PrescriptionContactWizard = (props: Props): JSX.Element => {
    const dispatch = useDispatch();

    const addPrescriptionContactBackLabel = getLabelFallback(
        'MYPROFILE_MY_PRESCRIPTIONS_BACK_TO_PRESCRIPTIONS_MOBILE'
    );
    const addPrescriptionContactTitle = props.isEditingPrescription
        ? getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_EDIT_PRESCRIPTION_TITLE')
        : getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_ADD_PRESCRIPTION_TITLE');

    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const mySavedPrescriptions = useSelector(
        (state: RootState) => state.myAppointment.mySavedPrescriptions
    );

    const match = useMatch(config.pagesUrl.myAppointments.editPrescriptionContact);

    const navigate = useNavigate();

    const [editedPrescriptionId, setEditedPrescriptionId] = useState(null);
    const [editedPrescription, setEditedPrescription] = useState<IMySavedPrescriptionItem>(null);
    const [editedRightCLDetails, setEditedRightCLDetails] = useState<IContactLensDetails>(null);
    const [isEditedRightCLCompatible, setIsEditedRightCLCompatible] = useState<boolean>(null);
    const [editedLeftCLDetails, setEditedLeftCLDetails] = useState<IContactLensDetails>(null);
    const [isEditedLeftCLCompatible, setIsEditedLeftCLCompatible] = useState<boolean>(null);
    const [hasRelatedOrderPrescriptions, setHasRelatedOrderPrescriptions] =
        useState<boolean>(false);
    const [step1FormData, setStep1FormData] = useState({
        checkedDifferentCL: false,
        selectedRightCL: null,
        selectedLeftCL: null,
    });
    const [goToStep, setGoToStep] = useState<number>(1);
    const [step, setStep] = useState<number>(1);
    const [isGoBackModalOpen, setIsGoBackModalOpen] = useState(false);
    const [backToSelectedCLRight, setBackToSelectedCLRight] = useState(null);
    const [backToSelectedCLLeft, setBackToSelectedCLLeft] = useState(null);
    const [backToStepTwoFirstName, setBackToStepTwoFirstName] = useState(null);
    const [backToStepTwoLastName, setBackToStepTwoLastName] = useState(null);
    const [backToStepTwoDob, setBackToStepTwoDob] = useState(null);
    const [backToStepTwoDoctor, setBackToStepTwoDoctor] = useState(null);
    const [backToStepTwoPowerOD, setBackToStepTwoPowerOD] = useState(null);
    const [backToStepTwoPowerOS, setBackToStepTwoPowerOS] = useState(null);
    const [backToStepTwoBcOD, setBackToStepTwoBcOD] = useState(null);
    const [backToStepTwoBcOS, setBackToStepTwoBcOS] = useState(null);
    const [backToStepTwoDiaOD, setBackToStepTwoDiaOD] = useState(null);
    const [backToStepTwoDiaOS, setBackToStepTwoDiaOS] = useState(null);
    const [backToStepTwoCylOD, setBackToStepTwoCylOD] = useState(null);
    const [backToStepTwoCylOS, setBackToStepTwoCylOS] = useState(null);
    const [backToStepTwoColorOD, setBackToStepTwoColorOD] = useState(null);
    const [backToStepTwoColorOS, setBackToStepTwoColorOS] = useState(null);
    const [backToStepTwoAxisOD, setBackToStepTwoAxisOD] = useState(null);
    const [backToStepTwoAxisOS, setBackToStepTwoAxisOS] = useState(null);
    const [backToStepTwoAddOD, setBackToStepTwoAddOD] = useState(null);
    const [backToStepTwoAddOS, setBackToStepTwoAddOS] = useState(null);
    const [backToStepTwoDomOD, setBackToStepTwoDomOD] = useState(null);
    const [backToStepTwoDomOS, setBackToStepTwoDomOS] = useState(null);
    const [isDifferentCLLeftFromBack, setIsDifferentCLLeftFromBack] = useState(false)
    const [isDifferentCLRightFromBack, setIsDifferentCLRightFromBack] = useState(false)

    /**
     * Fetch all saved prescriptions (for page reload)
     */
    useEffect(() => {
        if (!mySavedPrescriptions) {
            const mySavedPrescriptionsPayload: IMySavedPrescriptionsListPayload = {
                isMockRequest: config.mockRequest,
                storeId: config.storeId,
            };

            dispatch(getMySavedPrescriptions(mySavedPrescriptionsPayload));

            return () => {
                actions.getMySavedPrescriptionsSuccess(null);
            };
        }
    }, []);

    /*
     * Check if prescription id is passed as parameter in the URL (edit)
     */
    useEffect(() => {
        if (props.isEditingPrescription && !!match && match.params['id']) {
            setEditedPrescriptionId(match.params['id']);
        }
    }, [match]);

    /**
     * If prescription id is passed (edit), get the edited prescription data
     */
    useEffect(() => {
        if (props.isEditingPrescription && !!editedPrescriptionId && !!mySavedPrescriptions) {
            const currentPrescription = mySavedPrescriptions.find(
                element => element.prescriptionId.toString() === editedPrescriptionId.toString()
            );

            setEditedPrescription(currentPrescription);
            setHasRelatedOrderPrescriptions(currentPrescription.hasRelatedOrderPrescriptions);
        }
    }, [editedPrescriptionId, mySavedPrescriptions]);

    /**
     * Fetch the edited CLs details saved in the prescription (edit)
     */
    useEffect(() => {
        if (props.isEditingPrescription && !!editedPrescription) {
            fetchEditableCL(
                editedPrescription?.rightCatentryId,
                editedPrescription?.leftCatentryId
            );
        }
    }, [editedPrescription]);

    const fetchEditableCL = (rightCatentryId?: number, leftCatentryId?: number) => {
        let sameCatentryId: boolean = false;

        if (!!rightCatentryId && !!leftCatentryId) {
            sameCatentryId = rightCatentryId === leftCatentryId;
        }

        if (!!rightCatentryId) {
            getCLDetailsSingle(
                config.mockRequest,
                config.storeId,
                config.catalogId,
                rightCatentryId.toString()
            )
                .then((response: any) => {
                    if (!!response) {
                        console.log('Editable right CL', response);

                        setEditedRightCLDetails(response);

                        if (sameCatentryId) {
                            console.log('Editable left CL', response);

                            setEditedLeftCLDetails(response);
                        }
                    } else {
                        console.error('>>> Error on retrieve right CL details');
                    }
                })
                .catch(error => {
                    console.error(
                        '>>> Error on retrieve right CL details',
                        error.status,
                        ', error: ',
                        error
                    );
                });
        }

        if (!!leftCatentryId && !sameCatentryId) {
            getCLDetailsSingle(
                config.mockRequest,
                config.storeId,
                config.catalogId,
                leftCatentryId.toString()
            )
                .then((response: any) => {
                    if (!!response) {
                        console.log('Editable left CL', response);

                        setEditedLeftCLDetails(response);
                    } else {
                        console.error('>>> Error on retrieve right CL details');
                    }
                })
                .catch(error => {
                    console.error(
                        '>>> Error on retrieve right CL details',
                        error.status,
                        ', error: ',
                        error
                    );
                });
        }
    };

    useEffect(() => {
        /**
         * If edited CLs parent product id is different, set checkbox to checked
         */
        if (
            props.isEditingPrescription &&
            !!editedRightCLDetails &&
            !!editedLeftCLDetails &&
            editedRightCLDetails.parentId !== editedLeftCLDetails.parentId
        ) {
            setStep1FormData(step1FormData => ({
                ...step1FormData,
                checkedDifferentCL: true,
            }));
        }
    }, [editedRightCLDetails, editedLeftCLDetails]);

    useEffect(() => {
        /**
         * If editing prescription, check for compatibility between the edited prescription SKU and the selected product in the form
         */
        if (props.isEditingPrescription) {
            if (!!step1FormData.selectedRightCL && !!editedRightCLDetails) {
                if (step1FormData.selectedRightCL !== editedRightCLDetails?.parentId) {
                    console.log('Editable right CL not compatible with selected product');
                    setIsEditedRightCLCompatible(false);
                } else {
                    setIsEditedRightCLCompatible(true);
                }
            }

            if (!!step1FormData.selectedLeftCL && !!editedLeftCLDetails) {
                if (step1FormData.selectedLeftCL !== editedLeftCLDetails?.parentId) {
                    console.log('Editable left CL not compatible with selected product');
                    setIsEditedLeftCLCompatible(false);
                } else {
                    setIsEditedLeftCLCompatible(true);
                }
            }
        }
    }, [step1FormData]);

    useEffect(() => {
        /**
         * If editing prescription, wait for SKU compatibility checks before going to step 2
         */
        if (
            props.isEditingPrescription &&
            isEditedRightCLCompatible !== null &&
            isEditedLeftCLCompatible !== null &&
            goToStep === 2
        ) {
            setStep(2);
        } else if (!props.isEditingPrescription && goToStep === 2) {
            setStep(2);
        } else if (goToStep === 1){
            setStep(1);
        }
    }, [goToStep, isEditedRightCLCompatible, isEditedLeftCLCompatible]);

    const handleOpenGoBackModal = () => {
        setIsGoBackModalOpen(true);
        document.body.style.overflow = 'hidden'
    }

    const handleCloseGoBackModal = () => {
        setIsGoBackModalOpen(false);
        document.body.style.overflow = ''
    }

    const handleYesGoBackButton = () => {
        document.body.style.overflow = ''
        navigate(config.pagesUrl.myAppointments.myPrescriptions)
    }

    return (
        <PageLayout
            // @ts-ignore
            data_element_id="AccountSummary_AddPrescriptionContact"
            hideMenu
            labelMenu={addPrescriptionContactTitle}
            backLabel={addPrescriptionContactBackLabel}
            onBackButtonClick={() => handleOpenGoBackModal()}
        >
            <div
                className={
                    'add-prescription-contact' +
                    (props.isEditingPrescription ? ' edit-prescription' : '')
                }
            >
                {!isMobile && (
                    <div
                        role="region"
                        aria-label="contact lenses prescription form header"
                        className="add-prescription-contact-header"
                    >
                        <button className="back-button" data-element-id="MyPrescription_Back" onClick={() => handleOpenGoBackModal()}>
                            <span className="start-icon">
                                <ArrowLeft />
                            </span>
                            <span>{getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_BACK_TO_PRESCRIPTIONS')}</span>
                        </button>
                        <div className="primary-title">{addPrescriptionContactTitle}</div>
                    </div>
                )}

                {props.isEditingPrescription && hasRelatedOrderPrescriptions && (
                    <div className="prescription-values-warning popup-edit">
                        <div className='prescription-values-warning-icon'> <InfoIcon /></div>
                        <div className='prescription-values-warning-text'>
                            <span>{getLabelFallback('MYPROFILE_MY_PRESCRIPTION_EDIT_POPUP_1')}</span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: getLabelFallback('MYPROFILE_MY_PRESCRIPTION_EDIT_POPUP_2'),
                                }}
                            ></span>
                        </div>
                    </div>
                )}

                <div
                    className="add-prescription-contact-subtitle"
                    role="region"
                    aria-label="contact lenses prescription form step"
                >
                    {step === 2 && !isMobile && <button className='add-prescription-contact-subtitle-back-button' onClick={() => {
                         setGoToStep(1)
                    }}>
                        <BackButton />
                    </button>}
                    <div className="add-prescription-contact-subtitle-left">
                        {step === 2 && isMobile && <button className='add-prescription-contact-subtitle-back-button' onClick={() => {
                            setGoToStep(1)
                        }}>
                            <BackButtonMobile />
                        </button>}
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP')}
                        {' ' + step + ' '}
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_NUMBER')}
                    </div>
                    {!isMobile && <div className="add-prescription-contact-subtitle-center"></div>}
                    {!props.isEditingPrescription && <div className="add-prescription-contact-subtitle-right">
                        {getLabelFallback(
                            step === 1
                                ? 'MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_1_BRAND_SUBTITLE'
                                : 'MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_2_ENTER_PRESCRIPTION_SUBTITLE'
                        )}
                    </div>}
                    {props.isEditingPrescription && <div className="add-prescription-contact-subtitle-right">
                        {getLabelFallback(
                            step === 1
                                ? 'MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_1_BRAND_EDIT_SUBTITLE'
                                : 'MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_2_ENTER_PRESCRIPTION_SUBTITLE'
                        )}
                    </div>}
                </div>
                <div className="add-prescription-contact-container" role="form">
                    <div className="add-prescription-contact-container-title">
                        <ContactLenses />
                        <span>
                            {getLabelFallback(
                                'MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_CONTAINER_TITLE'
                            )}
                        </span>
                    </div>
                    {step === 1 ? (
                        <Step1Contact
                            isEditingPrescription={props.isEditingPrescription}
                            checkedDifferentCL={step1FormData.checkedDifferentCL}
                            editedRightCLDetails={editedRightCLDetails}
                            editedLeftCLDetails={editedLeftCLDetails}
                            onContinue={data => {
                                setStep1FormData({
                                    checkedDifferentCL: data.checkedDifferentCL,
                                    selectedRightCL: data.selectedRightCL,
                                    selectedLeftCL: data.selectedLeftCL,
                                });

                                setGoToStep(2);
                            }}
                            backToSelectedCLRight={backToSelectedCLRight}
                            setBackToSelectedCLRight={setBackToSelectedCLRight}
                            backToSelectedCLLeft={backToSelectedCLLeft}
                            setBackToSelectedCLLeft={setBackToSelectedCLLeft}
                            setIsDifferentClLeftFromBack={setIsDifferentCLLeftFromBack}
                            setIsDifferentClRightFromBack={setIsDifferentCLRightFromBack}
                        />
                    ) : (
                        <Step2Contact
                            isEditingPrescription={props.isEditingPrescription}
                            checkedDifferentCL={step1FormData.checkedDifferentCL}
                            selectedRightCL={step1FormData.selectedRightCL}
                            selectedLeftCL={step1FormData.selectedLeftCL}
                            editedPrescription={editedPrescription}
                            editedRightCLDetails={
                                isEditedRightCLCompatible ? editedRightCLDetails : null
                            }
                            editedLeftCLDetails={
                                isEditedLeftCLCompatible ? editedLeftCLDetails : null
                            }
                            backToStepTwoFirstName={backToStepTwoFirstName}
                            setBackToStepTwoFirstName={setBackToStepTwoFirstName}
                            backToStepTwoLastName={backToStepTwoLastName}
                            setBackToStepTwoLastName={setBackToStepTwoLastName}
                            backToStepTwoDob={backToStepTwoDob}
                            setBackToStepTwoDob={setBackToStepTwoDob}
                            backToStepTwoDoctor={backToStepTwoDoctor}
                            setBackToStepTwoDoctor={setBackToStepTwoDoctor}
                            backToStepTwoPowerOD={backToStepTwoPowerOD}
                            setBackToStepTwoPowerOD={setBackToStepTwoPowerOD}
                            backToStepTwoPowerOS={backToStepTwoPowerOS}
                            setBackToStepTwoPowerOS={setBackToStepTwoPowerOS}
                            backToStepTwoBcOD={backToStepTwoBcOD}
                            setBackToStepTwoBcOD={setBackToStepTwoBcOD}
                            backToStepTwoBcOS={backToStepTwoBcOS}
                            setBackToStepTwoBcOS={setBackToStepTwoBcOS}
                            backToStepTwoDiaOD={backToStepTwoDiaOD}
                            setBackToStepTwoDiaOD={setBackToStepTwoDiaOD}
                            backToStepTwoDiaOS={backToStepTwoDiaOS}
                            setBackToStepTwoDiaOS={setBackToStepTwoDiaOS}
                            backToStepTwoCylOD={backToStepTwoCylOD}
                            setBackToStepTwoCylOD={setBackToStepTwoCylOD}
                            backToStepTwoCylOS={backToStepTwoCylOS}
                            setBackToStepTwoCylOS={setBackToStepTwoCylOS}
                            backToStepTwoColorOD={backToStepTwoColorOD}
                            setBackToStepTwoColorOD={setBackToStepTwoColorOD}
                            backToStepTwoColorOS={backToStepTwoColorOS}
                            setBackToStepTwoColorOS={setBackToStepTwoColorOS}
                            backToStepTwoAxisOD={backToStepTwoAxisOD}
                            setBackToStepTwoAxisOD={setBackToStepTwoAxisOD}
                            backToStepTwoAxisOS={backToStepTwoAxisOS}
                            setBackToStepTwoAxisOS={setBackToStepTwoAxisOS}
                            backToStepTwoAddOD={backToStepTwoAddOD}
                            setBackToStepTwoAddOD={setBackToStepTwoAddOD}
                            backToStepTwoAddOS={backToStepTwoAddOS}
                            setBackToStepTwoAddOS={setBackToStepTwoAddOS}
                            backToStepTwoDomOD={backToStepTwoDomOD}
                            setBackToStepTwoDomOD={setBackToStepTwoDomOD}
                            backToStepTwoDomOS={backToStepTwoDomOS}
                            setBackToStepTwoDomOS={setBackToStepTwoDomOS}
                            isDifferentCLLeftFromBack={isDifferentCLLeftFromBack}
                            isDifferentCLRightFromBack={isDifferentCLRightFromBack}
                        />
                    )}
                </div>

                <Modal
                    customStyle={isMobile ? 'go-back-modal-sm' : 'go-back-modal-desk'}
                    isOpen={isGoBackModalOpen}
                    close={() => handleCloseGoBackModal()}
                    isCentered
                >
                    <div className="go-back-modal">
                    <div role="region" className="title">
                        {isMobile ? (
                            <>
                                {"Are you sure you want to"}
                                <br />
                                {"go back? Your changes will"}
                                <br />
                                {"not be saved."}
                            </>
                        ) : (
                            <>
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_GO_BACK_MODAL_TITLE_1')}
                                <br />
                                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_GO_BACK_MODAL_TITLE_2')}
                            </>
                        )}
                        </div>

                        <div className="buttons-container">
                            <button
                                role="region"
                                aria-label="Go back to prescription list button"
                                data-element-id="MyPrescription_BackOverlay-Confirm"
                                className={`primary-button ${
                                    isMobile ? 'button-size-small' : 'button-size-medium'
                                }`}
                                onClick={() => handleYesGoBackButton()}
                            >
                                {getLabelFallback(
                                    'MYPROFILE_MY_PRESCRIPTIONS_GO_BACK_MODAL_YES_LABEL'
                                )}
                            </button>
                            <button
                                role="region"
                                data-element-id="MyPrescription_BackOverlay-Cancel"
                                className={`primary-button-negative ${
                                    isMobile ? 'button-size-small' : 'button-size-medium'
                                }`}
                                onClick={() => handleCloseGoBackModal()}
                            >
                                {getLabelFallback(
                                    'MYPROFILE_MY_PRESCRIPTIONS_GO_BACK_MODAL_NO_LABEL'
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </PageLayout>
    );
};

export default PrescriptionContactWizard;
